import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getUserById } from '../services/userService';
import { acceptMessageRequest, rejectMessageRequest } from '../services/messageService';

const MessageRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionId, setActionId] = useState(null);
    const currentUid = auth.currentUser?.uid || null;

    useEffect(() => {
        if (!currentUid) {
            setRequests([]);
            setLoading(false);
            return () => {};
        }

        setLoading(true);
        let isActive = true;
        const q = query(collection(db, 'messageRequests'), where('receiverId', '==', currentUid));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                if (!isActive) return;
                
                (async () => {
                    const docs = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
                    const enriched = await Promise.all(
                        docs.map(async (req) => {
                            if (!req.senderId) return req;
                            try {
                                const sender = await getUserById(req.senderId);
                                return {
                                    ...req,
                                    senderName: sender?.name || sender?.displayName || 'Listener',
                                    senderPhoto: sender?.photoURL || null,
                                };
                            } catch {
                                return { ...req, senderName: 'Listener' };
                            }
                        })
                    );
                    enriched.sort((a, b) => {
                        const ta = a.timestamp?.toMillis ? a.timestamp.toMillis() : a.timestamp || 0;
                        const tb = b.timestamp?.toMillis ? b.timestamp.toMillis() : b.timestamp || 0;
                        return tb - ta;
                    });
                    if (isActive) {
                        setRequests(enriched.filter((item) => (item.status || 'pending') === 'pending'));
                        setLoading(false);
                    }
                })();
            },
            (err) => {
                console.error('Failed to load message requests', err);
                if (isActive) {
                    setRequests([]);
                    setLoading(false);
                }
            }
        );
        
        return () => {
            isActive = false;
            unsubscribe();
        };
    }, [currentUid]);

    const handleAccept = async (req) => {
        setActionId(req.id);
        try {
            await acceptMessageRequest(req);
        } catch (err) {
            console.error('Failed to accept message request', err);
            alert(err?.message || 'Failed to accept request. Please try again.');
        } finally {
            setActionId(null);
        }
    };

    const handleReject = async (req) => {
        setActionId(req.id);
        try {
            await rejectMessageRequest(req.id);
        } catch (err) {
            console.error('Failed to reject message request', err);
            alert(err?.message || 'Failed to reject request. Please try again.');
        } finally {
            setActionId(null);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        if (timestamp.toDate) {
            return timestamp.toDate().toLocaleString();
        }
        return new Date(timestamp).toLocaleString();
    };

    if (!currentUid) {
        return <div className="text-white/70 p-4">Sign in to view requests.</div>;
    }

    return (
        <div className="message-requests">
            {loading && <div className="text-center text-white/60 mt-8">Loading requests...</div>}
            {!loading && requests.length === 0 && (
                <div className="text-center text-white/60 mt-8">No pending requests</div>
            )}
            {requests.map((req) => (
                <div
                    key={req.id}
                    className="flex items-start gap-4 p-4 bg-[#1e1e1e] border border-[#2e2e2e] rounded-xl mb-3 hover:border-[#ff2a2a] hover:shadow-[0_0_15px_rgba(255,42,42,0.2)] transition"
                >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff2a2a] to-[#ff6b6b] flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {req.senderPhoto ? (
                            <img src={req.senderPhoto} alt={req.senderName || 'Sender'} className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-white font-bold text-lg">
                                {(req.senderName || 'U').charAt(0).toUpperCase()}
                            </span>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center mb-1">
                            <h3 className="font-semibold text-white">{req.senderName}</h3>
                            <span className="text-xs text-white/60">{formatDate(req.timestamp)}</span>
                        </div>
                        <p className="text-white/80 text-sm mb-3 leading-relaxed">{req.text}</p>
                        <div className="flex gap-2 flex-wrap">
                            <button
                                onClick={() => handleAccept(req)}
                                className="px-4 py-2 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg font-semibold hover:opacity-90 transition text-sm disabled:opacity-50"
                                disabled={actionId === req.id}
                            >
                                Accept
                            </button>
                            <button
                                onClick={() => handleReject(req)}
                                className="px-4 py-2 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-lg font-semibold hover:opacity-90 transition text-sm disabled:opacity-50"
                                disabled={actionId === req.id}
                            >
                                Reject
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default MessageRequests;

